import { useLanguage, t } from "../../../i18n";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Spinner, Form } from "react-bootstrap";
import { HiMapPin, HiOutlineMapPin, HiArrowPath, HiCheck } from "react-icons/hi2";
import { useLocation } from "../../../features/location/context/LocationContext";
import "./ManualLocationSelector.css";
function normalizeSearchText(value) {
  return String(value || "").trim().toLowerCase().replace(/\s+/g, " ");
}
export default function ManualLocationSelector({
  compact = false,
  onAfterSelect
}) {
  const {
    language
  } = useLanguage();
  const {
    cities,
    detectLocation,
    setLocation,
    isDetectingLocation,
    detectedCityId
  } = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [selectedCityId, setSelectedCityId] = useState(detectedCityId ? String(detectedCityId) : "");
  const [searchTerm, setSearchTerm] = useState("");
  const [cityFieldTouched, setCityFieldTouched] = useState(false);
  const [isSavingManualCity, setIsSavingManualCity] = useState(false);
  const searchInputRef = useRef(null);
  const cityOptions = useMemo(() => {
    return Array.isArray(cities) ? cities.filter(item => item?.id && item?.name).map(item => ({
      id: String(item.id),
      name: item.name,
      nameAr: item.name_ar || "",
      stateId: item.state_id || ""
    })) : [];
  }, [cities]);
  const filteredCityOptions = useMemo(() => {
    const normalizedSearch = normalizeSearchText(searchTerm);
    if (!normalizedSearch) {
      return cityOptions;
    }
    return cityOptions.filter(city => {
      const searchableText = normalizeSearchText(`${city.name} ${city.nameAr}`);
      return searchableText.includes(normalizedSearch);
    });
  }, [cityOptions, searchTerm]);
  const selectedCity = useMemo(() => {
    return cityOptions.find(item => String(item.id) === String(selectedCityId)) || null;
  }, [cityOptions, selectedCityId]);
  const cityError = cityFieldTouched && !selectedCityId ? "Please select a city." : "";
  useEffect(() => {
    if (showModal) {
      const timeout = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 150);
      return () => clearTimeout(timeout);
    }
  }, [showModal]);
  const handleUseMyLocation = async () => {
    try {
      await detectLocation(cities);
      if (onAfterSelect) {
        onAfterSelect();
      }
    } catch (error) {

      // handled by context
    }
  };
  const handleOpenModal = () => {
    setSelectedCityId(detectedCityId ? String(detectedCityId) : "");
    setSearchTerm("");
    setCityFieldTouched(false);
    setShowModal(true);
  };
  const handleCloseModal = () => {
    if (isSavingManualCity) return;
    setShowModal(false);
    setSearchTerm("");
    setCityFieldTouched(false);
  };
  const handleSelectCity = cityId => {
    setSelectedCityId(String(cityId));
    setCityFieldTouched(true);
  };
  const handleSaveManualCity = async () => {
    setCityFieldTouched(true);
    if (!selectedCityId) {
      return;
    }
    const matchedCity = cityOptions.find(item => String(item.id) === String(selectedCityId));
    if (!matchedCity) return;
    try {
      setIsSavingManualCity(true);
      setLocation({
        cityId: matchedCity.id,
        city: matchedCity.name,
        cityAr: matchedCity.nameAr || "",
        stateId: matchedCity.stateId || "",
        country: "Saudi Arabia",
        sourceState: "",
        sourceArea: "",
        sourceDistrict: "",
        sourceFormattedAddress: matchedCity.name,
        detectedAt: Date.now(),
        isResolvedFromMasterCity: true
      });
      setShowModal(false);
      setSearchTerm("");
      setCityFieldTouched(false);
      if (onAfterSelect) {
        onAfterSelect();
      }
    } finally {
      setIsSavingManualCity(false);
    }
  };
  return <>
      <div className={`bb-manual-location-selector ${compact ? "bb-manual-location-selector--compact" : ""}`}>

        <button type="button" className="bb-manual-location-selector__action bb-manual-location-selector__action--detect" onClick={handleUseMyLocation} disabled={isDetectingLocation}>

          {isDetectingLocation ? <>
              <Spinner as="span" animation="border" size="sm" className="me-2" />{t("Detecting...")}


          </> : <>
              <HiArrowPath className="bb-manual-location-selector__icon" />
              <span>{t("Use My Location")}</span>
            </>}
        </button>

        <button type="button" className="bb-manual-location-selector__action bb-manual-location-selector__action--choose" onClick={handleOpenModal}>

          <HiOutlineMapPin className="bb-manual-location-selector__icon" />
          <span>{t("Choose City")}</span>
        </button>
      </div>

      <Modal show={showModal} onHide={handleCloseModal} centered dialogClassName="bb-manual-location-modal">

        <Modal.Header closeLabel={t("Close")} closeButton={!isSavingManualCity}>
          <Modal.Title className="bb-manual-location-modal__title">{t("Select Your City")}

          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="bb-manual-location-modal__intro">
            <div className="bb-manual-location-modal__icon-wrap">
              <HiMapPin />
            </div>
            <p className="bb-manual-location-modal__text">{t("Choose your city manually if location detection is unavailable or inaccurate.")}

            </p>
          </div>

          <Form.Group>
            <Form.Label className="bb-manual-location-modal__label">{t("City ")}
              <span className="bb-required-mark">*</span>
            </Form.Label>

            <div className="bb-manual-location-search">
              <Form.Control ref={searchInputRef} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder={t("Search city")} className="bb-manual-location-search__input" disabled={isSavingManualCity} />


              <div className="bb-manual-location-search__dropdown">
                {filteredCityOptions.length ? filteredCityOptions.map(city => {
                const isSelected = String(city.id) === String(selectedCityId);
                return <button type="button" key={city.id} className={`bb-manual-location-search__option ${isSelected ? "bb-manual-location-search__option--active" : ""}`} onClick={() => handleSelectCity(city.id)} disabled={isSavingManualCity}>

                        <span className="bb-manual-location-search__option-text">
                          {language === "ar" ? city.nameAr || t(city.name) : city.name}
                        </span>

                        {isSelected ? <HiCheck className="bb-manual-location-search__option-icon" /> : null}
                      </button>;
              }) : <div className="bb-manual-location-search__empty">{t("No relevant city found")}

                </div>}
              </div>
            </div>

            {selectedCity ? <div className="bb-manual-location-search__selected">{t("Selected: ")}
              <strong>{language === "ar" ? selectedCity.nameAr || t(selectedCity.name) : selectedCity.name}</strong>
              </div> : null}

            {cityError ? <div className="bb-manual-location-search__error">
                {t(cityError)}
              </div> : null}
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="bb-manual-location-modal__footer">
          <Button variant="outline-secondary" onClick={handleCloseModal} disabled={isSavingManualCity} className="bb-manual-location-modal__cancel">{t("Cancel")}


          </Button>

          <Button variant="primary" onClick={handleSaveManualCity} disabled={!selectedCityId || isSavingManualCity} className="bb-manual-location-modal__save">

            {isSavingManualCity ? <>
                <Spinner as="span" animation="border" size="sm" className="me-2" />{t("Saving...")}


            </> : t("Save City")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>;
}
